<template>
  <div class="modal-card" style="width: 800px">
    <header class="modal-card-head">
      <p class="modal-card-title">Example Editor</p>
      <button type="button" class="delete" @click="close" aria-label="close" />
    </header>

    <section class="modal-card-body">
      <b-field
        :label="pgnLabel"
        label-position="on-border"
        :type="validationMessage ? 'is-danger' : ''"
        :message="validationMessage"
      >
        <b-input
          ref="pgnInput"
          v-model="pgnText"
          type="textarea"
          :placeholder="
            originalScenario
              ? 'Enter PGN notation'
              : 'Enter up to 50 PGN games, separated by blank lines'
          "
          rows="15"
          class="pgn-textarea monospace"
          :class="{ 'is-danger': validationMessage }"
        />
      </b-field>

      <div class="is-flex mb-3">
        <b-field
          label="Topic"
          label-position="on-border"
          class="is-flex-grow-1 mb-0"
        >
          <b-select v-model="selectedTopicId" expanded>
            <option v-for="topic in topics" :key="topic.id" :value="topic.id">
              {{ topic.isPublic ? "🌍" : "🔒" }} {{ topic.title }}
            </option>
          </b-select>
        </b-field>

        <b-button
          type="is-primary is-light"
          icon-left="plus"
          class="ml-2"
          style="align-self: flex-end"
          @click="isTopicEditorActive = true"
        />
      </div>

      <div class="is-flex is-justify-content-space-between mb-3">
        <!-- Order Field -->
        <b-field label="Order" label-position="on-border" class="is-flex">
          <b-input
            v-model.number="selectedOrder"
            type="number"
            min="0"
            :disabled="!originalScenario"
            style="width: 120px"
          />
          <b-button
            class="ml-2"
            type="is-info is-light"
            :disabled="!originalScenario"
            @click="handleReorder"
          >
            Reorder
          </b-button>
        </b-field>

        <!-- Updated Date -->
        <div
          v-if="originalScenario && originalScenario.updatedAt"
          class="update-info"
        >
          <b-tooltip
            :label="originalScenario.updatedAt | asDateTime"
            position="is-bottom"
            type="is-light"
          >
            <span class="has-text-grey is-size-6">
              Updated {{ originalScenario.updatedAt | distanceFromNow }}
            </span>
          </b-tooltip>
        </div>

        <!-- Status Field -->
        <b-field label="Status" label-position="on-border">
          <b-select v-model="selectedStatus">
            <option
              v-for="status in statusOptions"
              :key="status"
              :value="status"
            >
              {{ status }}
            </option>
          </b-select>
        </b-field>
      </div>

      <div v-if="showWarning" class="notification is-warning">
        <p>
          Warning: This example already exists in topic "{{
            currentTopicTitle
          }}"
        </p>
        <p>
          You can either save it as a new example or update the existing one.
        </p>
      </div>
    </section>

    <footer class="modal-card-foot">
      <b-button
        label="Load as Custom Example"
        type="is-primary"
        :disabled="!isValidInput"
        @click="loadScenarioFromPgn"
      />

      <b-button
        label="Save to Topic"
        type="is-info"
        :disabled="!isValidInput || !canSaveAsNew"
        @click="saveScenarioToTopic"
      />

      <b-button
        v-if="canUpdate"
        label="Update Example"
        type="is-warning"
        :disabled="!isValidInput || !hasChanges"
        @click="updateScenario"
      />

      <b-button label="Cancel" @click="close" />
    </footer>

    <!-- Topic Editor Modal -->
    <b-modal
      v-model="isTopicEditorActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
    >
      <TopicEditor
        @saved="handleTopicSaved"
        @close="isTopicEditorActive = false"
      />
    </b-modal>
  </div>
</template>

<script>
import { TopicsService } from "@/services/CoachingServices";
import { ContentStatus } from "vue-chess-shared";
import { PgnParser } from "vue-chess-shared/pgn/PgnParser";
import TopicEditor from "@/components/training/topics/TopicEditor.vue";
import topicLoader from "@/mixins/topicLoader";

export default {
  name: "ScenarioEditor",

  components: {
    TopicEditor,
  },

  mixins: [topicLoader],

  props: {
    initialPgn: {
      type: String,
      default: "",
    },
    originalScenario: {
      type: Object,
      default: null,
    },
    currentTopicId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      pgnText: "",
      selectedStatus: ContentStatus.Published,
      selectedOrder: 0,
      isValidPgn: true,
      isTopicEditorActive: false,
      statusOptions: Object.values(ContentStatus),
    };
  },

  computed: {
    isValidInput() {
      if (this.originalScenario) {
        // In edit mode - validate single PGN
        return this.isValidPgn && !!this.pgnText.trim();
      } else {
        // In create mode - validate multiple PGNs
        const pgns = PgnParser.splitGames(this.pgnText);
        if (pgns.length > 50) {
          return false;
        }
        return (
          pgns.length > 0 && pgns.every((pgn) => PgnParser.isValidPgn(pgn))
        );
      }
    },

    validationMessage() {
      if (!this.pgnText.trim()) {
        return "Please enter PGN notation";
      }

      if (!this.originalScenario) {
        const pgns = PgnParser.splitGames(this.pgnText);
        if (pgns.length > 50) {
          return "Maximum 50 examples allowed";
        }
        if (!pgns.every((pgn) => PgnParser.isValidPgn(pgn))) {
          return "Invalid PGN format";
        }
      } else if (!this.isValidPgn) {
        return "Invalid PGN format";
      }

      return "";
    },

    isSameTopic() {
      return (
        this.selectedTopicId &&
        this.originalScenario?.topicId === this.selectedTopicId
      );
    },

    showWarning() {
      return this.originalScenario && this.isSameTopic;
    },

    canSaveAsNew() {
      return this.selectedTopicId && !this.isSameTopic;
    },

    canUpdate() {
      return this.originalScenario && this.isSameTopic;
    },

    currentTopicTitle() {
      const topic = this.topics.find((t) => t.id === this.selectedTopicId);
      return topic?.title || "";
    },

    hasChanges() {
      if (!this.originalScenario) return true;

      return (
        this.pgnText.trim() !== this.originalScenario.pgn.trim() ||
        this.selectedTopicId !== this.originalScenario.topicId ||
        this.selectedStatus !== this.originalScenario.status ||
        this.selectedOrder !== this.originalScenario.order
      );
    },

    pgnCount() {
      if (!this.pgnText.trim()) return 0;
      return PgnParser.splitGames(this.pgnText).length;
    },

    pgnLabel() {
      if (this.originalScenario) return "PGN";
      return `PGN (${this.pgnCount}/50 examples)`;
    },
  },

  watch: {
    initialPgn: {
      immediate: true,
      handler(newPgn) {
        this.pgnText = newPgn;
      },
    },

    originalScenario: {
      immediate: true,
      handler(scenario) {
        if (scenario) {
          this.pgnText = this.pgnText || scenario.pgn;
          this.selectedTopicId = scenario.topicId;
          this.selectedStatus = scenario.status;
          this.selectedOrder = scenario.order;
        } else {
          this.selectedOrder = 0; // Reset order for new examples
        }
      },
    },

    currentTopicId: {
      immediate: true,
      handler(newTopicId) {
        if (newTopicId && !this.originalScenario) {
          // Only set if we're creating a new example
          this.selectedTopicId = newTopicId;
        }
      },
    },
  },

  async created() {
    await this.loadTopics();

    // Set initial topic ID in order of priority:
    // 1. Original example's topic
    // 2. Current selected topic
    // 3. First available topic
    this.selectedTopicId =
      this.originalScenario?.topicId ||
      this.currentTopicId ||
      (this.topics.length > 0 ? this.topics[0].id : null);
  },

  mounted() {
    // Focus the PGN input when the component is mounted
    this.$nextTick(() => {
      this.$refs.pgnInput.focus();
    });
  },

  methods: {
    close() {
      this.$emit("close");
    },

    loadScenarioFromPgn() {
      this.$emit("loadScenarioFromPgn", this.pgnText);
      this.close();
    },

    saveScenarioToTopic() {
      this.$emit("saveScenarioToTopic", {
        topicId: this.selectedTopicId,
        pgn: this.pgnText,
        status: this.selectedStatus,
      });
      this.close();
    },

    updateScenario() {
      if (!this.originalScenario) return;

      this.$emit("updateScenario", {
        scenarioId: this.originalScenario.id,
        topicId: this.selectedTopicId,
        pgn: this.pgnText,
        status: this.selectedStatus,
        order: this.selectedOrder,
      });
      this.close();
    },

    async handleTopicSaved(topic) {
      await this.loadTopics();
      this.isTopicEditorActive = false;
      this.selectedTopicId =
        topic?.id || this.topics[this.topics.length - 1].id;
    },

    async handleReorder() {
      if (!this.originalScenario) return;

      try {
        const scenarios = await TopicsService.getScenarios(
          this.selectedTopicId
        );
        const currentIndex = scenarios.findIndex(
          (s) => s.id === this.originalScenario.id
        );
        const targetIndex = scenarios.findIndex(
          (s) => s.order > this.selectedOrder
        );

        if (currentIndex !== -1) {
          const newScenarios = await TopicsService.reorderScenarios(
            this.selectedTopicId,
            currentIndex,
            targetIndex === -1 ? scenarios.length : targetIndex
          );

          // Update the current example with new order
          const updatedScenario = newScenarios.find(
            (s) => s.id === this.originalScenario.id
          );
          if (updatedScenario) {
            this.selectedOrder = updatedScenario.order;
          }

          this.$buefy.toast.open({
            message: "Examples reordered successfully",
            type: "is-success",
          });
        }
      } catch (error) {
        console.error("Error reordering examples:", error);
        this.$buefy.toast.open({
          message: "Failed to reorder examples",
          type: "is-danger",
        });
      }
    },
  },
};
</script>

<style scoped>
.pgn-textarea {
  font-family: monospace;
  font-size: 14px;
}
</style>
